import { useClerk, useUser } from '@clerk/clerk-react'
import { useStatsigClient } from '@statsig/react-bindings'
import { useEffect, useRef } from 'react'
import { isIdleSignOutEnabled } from '../utility/releaseStatsigGateConstants'
import { signOutCurrentUser } from '../utility/signOutUser'

/** Matches the Clerk inactivity timeout, which never fires while a tab keeps refreshing its token. */
export const IDLE_SIGN_OUT_TIMEOUT_MS = 2 * 60 * 60 * 1000
export const IDLE_CHECK_INTERVAL_MS = 60 * 1000
/** Shared across tabs, so activity in any tab keeps every tab signed in. */
export const LAST_ACTIVITY_STORAGE_KEY = 'contxt-last-activity-at'
const ACTIVITY_WRITE_THROTTLE_MS = 15 * 1000
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart']

export const readLastActivityAt = () => {
  try {
    const value = Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY))
    return Number.isFinite(value) && value > 0 ? value : null
  } catch {
    return null
  }
}

export const writeLastActivityAt = (timestamp) => {
  try {
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, String(timestamp))
  } catch {
    // ignore private-mode / quota errors; this tab still tracks activity in memory
  }
}

/**
 * Signs the user out after `timeoutMs` with no mouse, keyboard, scroll or touch activity in any
 * open CONTXT tab. Runs only while the `cc-7175-fe-idle-sign-out` gate is on.
 *
 * @param {object} [options]
 * @param {number} [options.timeoutMs]
 */
export function useIdleSignOut({ timeoutMs = IDLE_SIGN_OUT_TIMEOUT_MS } = {}) {
  const clerk = useClerk()
  const { user, isSignedIn } = useUser()
  const { client } = useStatsigClient()
  const enabled = Boolean(isSignedIn) && isIdleSignOutEnabled(client)

  const userRef = useRef(user)
  userRef.current = user

  useEffect(() => {
    if (!enabled) return undefined

    let lastActivityAt = Date.now()
    let lastWriteAt = 0
    let signingOut = false

    const recordActivity = () => {
      const now = Date.now()
      lastActivityAt = now
      if (now - lastWriteAt >= ACTIVITY_WRITE_THROTTLE_MS) {
        lastWriteAt = now
        writeLastActivityAt(now)
      }
    }

    const checkIdle = () => {
      if (signingOut) return
      const sharedLastActivityAt = readLastActivityAt() ?? lastActivityAt
      const latestActivityAt = Math.max(sharedLastActivityAt, lastActivityAt)
      if (Date.now() - latestActivityAt < timeoutMs) return

      signingOut = true
      signOutCurrentUser({ clerk, user: userRef.current }).then((ok) => {
        if (!ok) signingOut = false
      })
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkIdle()
    }

    // Loading the app counts as activity, so a stale value from an earlier session can't sign the user out.
    recordActivity()

    ACTIVITY_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, recordActivity, { capture: true, passive: true }),
    )
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', checkIdle)
    const intervalId = window.setInterval(checkIdle, IDLE_CHECK_INTERVAL_MS)

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, recordActivity, { capture: true }),
      )
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', checkIdle)
      window.clearInterval(intervalId)
    }
  }, [enabled, clerk, timeoutMs])
}
